import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Check, MapPin, ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import { findPlace, placesOfRegion, SERVICE_SEO, useSeo, localBusinessLd } from "../lib/seo";
import local from "../data/localContent.json";
import NotFound from "./NotFound";
import { serviceIcon } from "../lib/icons";

const fill = (text, place) =>
  (text || "")
    .replaceAll("{ort}", place.name)
    .replaceAll("{kreis}", place.kreis)
    .replaceAll("{region}", place.regionName);

const shortName = (name, place) =>
  name.startsWith(`${place.name}-`) ? name.slice(place.name.length + 1) : name;

const STEPS = [
  "Anfrage mit Ihren Angaben senden",
  "Fotos übermitteln, falls vorhanden",
  "Einschätzung des Aufwands durch NENOX",
  "Bei Bedarf kostenlose Besichtigung vor Ort",
  "Individuelles Angebot erhalten",
  "Durchführung der Arbeiten zum vereinbarten Termin",
];

export default function LeistungOrt() {
  const { slug, ort } = useParams();
  const place = findPlace(ort);
  const [svc, setSvc] = useState(null);
  const [services, setServices] = useState([]);
  const seo = SERVICE_SEO[slug] || {};
  const content = local.services[slug];
  const enhanced = local.enabled_combos.includes(`${slug}/${ort}`) && !!content;
  const label = seo.label || svc?.name || "";

  useEffect(() => {
    api.get(`/services/${slug}`).then((r) => setSvc(r.data)).catch(() => setSvc(false));
    api.get("/services").then((r) => setServices(r.data)).catch(() => setServices([]));
  }, [slug]);

  useSeo({
    noindex: !place || svc === false,
    title: place && label ? `${label} ${place.name}` : "Seite nicht gefunden",
    description:
      place && label
        ? enhanced
          ? fill(content.desc, place)
          : `${label} in ${place.name} und Umgebung von NENOX – Angaben online eingeben, Fotos hochladen und kostenloses Angebot erhalten.`
        : undefined,
    canonical: place ? `/leistungen/${slug}/${place.slug}` : undefined,
    jsonLd:
      place && label
        ? {
            "@context": "https://schema.org",
            "@type": "Service",
            serviceType: `${label} ${place.name}`,
            areaServed: { "@type": "City", name: place.name },
            provider: localBusinessLd(),
          }
        : null,
  });

  if (!place || svc === false) return <NotFound />;
  if (!svc) return <div className="max-w-3xl mx-auto px-6 py-32 text-zinc-400">Laden…</div>;

  const Icon = serviceIcon(svc.icon);
  const sameRegion = placesOfRegion(place.regionSlug).filter((p) => p.slug !== place.slug).slice(0, 10);
  const otherServices = services.filter((s) => s.slug !== slug);
  const placeNote = local.places[place.slug];
  const comboNote = (local.combos || {})[`${slug}/${ort}`] ||
    ((local.place_profiles || {})[place.slug] || {})[(local.theme_map || {})[slug]];
  const externalNearby = sameRegion.filter((p) => !p.name.startsWith(`${place.name}-`));

  const Section = ({ title, children }) => (
    <section className="mt-16">
      <h2 className="nx-head text-2xl md:text-3xl tracking-tight font-medium">{title}</h2>
      <div className="mt-5 text-sm md:text-base text-zinc-400 max-w-3xl leading-relaxed space-y-4">{children}</div>
    </section>
  );

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-16" data-testid="leistung-ort-page">
      <div className="flex flex-wrap items-center gap-2 text-xs uppercase tracking-[0.2em] text-zinc-400">
        <Link to="/leistungen">Leistungen</Link> ·
        <Link to={`/leistungen/${slug}`} data-testid="breadcrumb-service">{svc.name}</Link> ·
        <Link to={`/einsatzgebiete/${place.slug}`} data-testid="breadcrumb-place" className="inline-flex items-center gap-1">
          <MapPin size={12} /> {place.name}
        </Link>
      </div>

      <Icon size={32} strokeWidth={1.2} className="mt-8 text-zinc-400" />
      <h1 className="nx-head mt-5 text-4xl md:text-6xl tracking-tighter font-medium">
        {enhanced ? `${label} in ${place.name}` : `${label} ${place.name}`}
      </h1>

      <p className="mt-6 text-sm md:text-base text-zinc-400 max-w-3xl leading-relaxed" data-testid="local-intro">
        {enhanced ? fill(content.intro, place) : seo.text}
      </p>

      <div className="mt-10 flex flex-col sm:flex-row gap-4">
        <Link to={`/anfrage/${slug}?ort=${place.slug}`} data-testid="leistung-ort-cta" className="nx-btn-action">
          {svc.quote_mode === "richtpreis" ? "Richtpreis berechnen" : "Angebot anfragen"}
        </Link>
        <Link to="/besichtigung" className="nx-btn-ghost">Besichtigung vereinbaren</Link>
      </div>

      <Section title="Unsere Leistung">
        {enhanced ? (
          <>
            <p>{fill(content.objects, place)}</p>
            <ul className="grid gap-3 sm:grid-cols-2">
              {content.focus.map((f) => (
                <li key={f} className="flex items-start gap-3 border-b border-white/15 pb-3">
                  <Check size={16} className="mt-0.5 text-zinc-400 shrink-0" /> {f}
                </li>
              ))}
            </ul>
            <p>{fill(content.calc, place)}</p>
            {comboNote && <p data-testid="combo-note">{comboNote}</p>}
          </>
        ) : (
          <ul className="grid gap-3 sm:grid-cols-2">
            {(svc.items || []).map((i) => (
              <li key={i} className="flex items-start gap-3 border-b border-white/15 pb-3">
                <Check size={16} className="mt-0.5 text-zinc-400 shrink-0" /> {i}
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title="Einsatzgebiet">
        {placeNote && <p data-testid="place-note">{placeNote}</p>}
        <p>
          Wir bieten diese Leistung in {place.name}
          {!place.kreis.includes(place.name) && <> ({place.kreis})</>} und der näheren Umgebung an. Je nach Auftrag
          fahren wir auch umliegende Orte und Gemeinden der Region {place.area} an
          {externalNearby.length > 0 && <> – zum Beispiel {externalNearby.slice(0, 4).map((p) => p.name).join(", ")}</>}.
        </p>
      </Section>

      <Section title="So läuft es ab">
        <ol className="space-y-3">
          {STEPS.map((s, i) => (
            <li key={s} className="flex gap-4 border-b border-white/15 pb-3">
              <span className="nx-head text-sm text-zinc-400 w-7">{String(i + 1).padStart(2, "0")}</span>
              <span>{s}</span>
            </li>
          ))}
        </ol>
      </Section>

      <Section title="Weitere Leistungen">
        <div className="flex flex-wrap gap-3">
          {otherServices.map((s) => (
            <Link
              key={s.slug}
              to={`/leistungen/${s.slug}/${place.slug}`}
              data-testid={`other-service-${s.slug}`}
              className="border border-white/15 px-4 py-3 sm:py-2 text-sm hover:bg-white/5"
              style={{ transition: "background-color .2s ease" }}
            >
              {(SERVICE_SEO[s.slug] || {}).label || s.name}
            </Link>
          ))}
        </div>
        <p className="text-sm">
          Übergeordnet: <Link to={`/leistungen/${slug}`} className="underline">{svc.name}</Link> ·{" "}
          <Link to={`/einsatzgebiete/${place.slug}`} className="underline">Einsatzgebiet {place.name}</Link>
        </p>
      </Section>

      {sameRegion.length > 0 && (
        <Section title="In der Umgebung">
          <p>Die gleiche Leistung bieten wir auch in diesen Orten an:</p>
          <div className="flex flex-wrap gap-3">
            {sameRegion.map((p) => (
              <Link
                key={p.slug}
                to={`/leistungen/${slug}/${p.slug}`}
                data-testid={`nearby-${slug}-${p.slug}`}
                className="border border-white/15 px-4 py-3 sm:py-2 text-sm hover:bg-white/5"
                style={{ transition: "background-color .2s ease" }}
              >
                {shortName(p.name, place)}
              </Link>
            ))}
          </div>
        </Section>
      )}

      <section className="mt-16 nx-card p-7 md:p-10">
        <h2 className="nx-head text-2xl tracking-tight font-medium">Ihre Anfrage</h2>
        <p className="mt-4 text-sm text-zinc-400 max-w-2xl">
          Geben Sie Ihre Angaben online ein und laden Sie auf Wunsch Fotos hoch – Sie erhalten ein individuelles
          Angebot. Ist der Aufwand aus der Ferne nicht sicher einschätzbar, schauen wir uns das Objekt kostenlos
          vor Ort an. Wir sind rund um die Uhr erreichbar.
        </p>
        <div className="mt-8 flex flex-col sm:flex-row gap-4">
          <Link to={`/anfrage/${slug}?ort=${place.slug}`} data-testid="bottom-cta" className="nx-btn-action">
            Angebot anfragen <ArrowRight size={16} />
          </Link>
          <Link to="/besichtigung" className="nx-btn-ghost">Besichtigung vereinbaren</Link>
        </div>
      </section>
    </div>
  );
}
